import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAppData } from '@/app-data';
import { useDocumentTitle } from '@/utils/use-document-title';
import * as api from '@/features/backlog/api';
import { useLiveItems } from '@/features/realtime/realtime';
import { Item } from '@/features/backlog/types';
import { boardPath, taskPath } from '@/constants/routes';
import { cn } from '@/components/ui/cn';
import ItemDrawer from '@/components/item-drawer';
import { Spinner } from '@/components/ui/Spinner';
import { Chevron } from '@/components/ui/icons';

/**
 * Full-screen task page at /:slug/:itemKey (e.g. /spark-product-backlog/SPB-3). Resolves the item
 * by its human key within the board, then renders the item details full-width by reusing the
 * ItemDrawer in its `page` presentation (same properties / description / comments / activity /
 * attachments / autosave as the quick-edit drawer — they coexist).
 */
export default function TaskPage() {
  const { boardSlug = '', itemKey = '' } = useParams();
  const { currentBoard, loading: appLoading } = useAppData();
  const navigate = useNavigate();
  // A notification click deep-links with ?hl=<target> (comment:<id> | field:<name> | attachments):
  // pass it to the drawer, which opens the right tab, scrolls to it and flashes it.
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get('hl') ?? undefined;
  const openAttachment = searchParams.get('att') ?? undefined;
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const boardId = currentBoard?.id ?? null;

  const reload = useCallback(async () => {
    if (!boardId) return;
    const res = await api.listItems({ filter: [{ name: 'board_id', op: 'eq', value: boardId }], limit: 2000 });
    setItems(res.data);
  }, [boardId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      if (boardId) await reload();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [boardId, reload]);

  // Apply create/update/move/delete from other tabs & users live (and re-fetch after a reconnect) so
  // the full-screen page — its breadcrumb, the item's fields, and its sub-task tree — stays current
  // without a manual refresh, exactly like the board views.
  useLiveItems(boardId, setItems, reload);

  const item = useMemo(
    () => items.find((i) => (i.key || '').toLowerCase() === itemKey.toLowerCase()) ?? null,
    [items, itemKey],
  );
  useDocumentTitle(item ? `${item.key} ${item.title}` : itemKey);

  // The parent chain (root → immediate parent) for breadcrumbs, so the nesting is navigable.
  const ancestry = useMemo(() => {
    if (!item) return [] as Item[];
    const byId = new Map(items.map((i) => [i.id, i]));
    const chain: Item[] = [];
    let cur = item.parent_id ? byId.get(item.parent_id) : undefined;
    let guard = 0;
    while (cur && guard++ < 50) {
      chain.unshift(cur);
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    }
    return chain;
  }, [items, item]);

  const back = () => navigate(boardPath(boardSlug));

  // Unknown board slug (once loaded) → back to the boards index.
  if (!appLoading && !currentBoard) return <Navigate to="/" replace />;

  if (appLoading || loading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  const crumb = 'max-w-[220px] truncate text-fg-muted transition-colors hover:text-fg';
  const sep = <Chevron className="h-3 w-3 shrink-0 text-fg-ghost" aria-hidden />;

  return (
    // One centered column so the breadcrumb, the actions row and the card all share a left edge.
    <div className="mx-auto w-full max-w-[1100px] min-[1440px]:max-w-[1560px]">
      {/* Breadcrumbs: Boards ▸ board ▸ parent chain ▸ this item — navigable + gives nesting context. */}
      <nav aria-label="Breadcrumb" className="mb-5 flex flex-wrap items-center gap-1.5 text-[13px]">
        <Link to="/" className={crumb}>
          Boards
        </Link>
        {sep}
        <Link to={boardPath(boardSlug)} className={crumb}>
          {currentBoard?.name ?? 'Board'}
        </Link>
        {ancestry.map((a) => (
          <span key={a.id} className="flex items-center gap-1.5">
            {sep}
            <Link to={taskPath(boardSlug, a.key)} className={cn(crumb, 'inline-flex items-center gap-1.5')} title={a.title}>
              <span className="item-key shrink-0 rounded-md px-1.5 py-0.5 tnum text-[11px] font-semibold">{a.key}</span>
              <span className="truncate">{a.title}</span>
            </Link>
          </span>
        ))}
        {item ? (
          <>
            {sep}
            <span className="inline-flex items-center gap-1.5 font-medium text-fg-strong">
              <span className="item-key shrink-0 rounded-md px-1.5 py-0.5 tnum text-[11px] font-semibold">{item.key}</span>
              <span className="max-w-[280px] truncate">{item.title}</span>
            </span>
          </>
        ) : null}
      </nav>

      {item ? (
        <ItemDrawer
          presentation="page"
          open
          itemId={item.id}
          boardId={boardId!}
          items={items}
          highlight={highlight}
          openAttachment={openAttachment}
          onClose={back}
          onSaved={reload}
        />
      ) : (
        <div className="mx-auto max-w-[560px] rounded-2xl border border-line bg-surface-raised px-6 py-16 text-center">
          <div className="text-[15px] font-semibold text-fg-strong">
            No task <span className="tnum">{itemKey}</span> on this board
          </div>
          <p className="mx-auto mt-2 max-w-[340px] text-[13px] leading-relaxed text-fg-faint">
            It may have been deleted or moved, or the key is wrong. Head back to the board to find it.
          </p>
        </div>
      )}
    </div>
  );
}
